/**
 * VoiceDebugPanel — painel visual de diagnóstico do pipeline de voz.
 *
 * Renderizado dentro do ImmersiveVoiceOverlay. Lê window.__nexoVoiceDebug()
 * a cada 150ms e exibe estado vivo de:
 *   - state machine (IDLE/LISTENING/PROCESSING/SPEAKING)
 *   - prewarm + mic track enabled/label
 *   - VAD speaking
 *   - permissão da mic + suporte MediaDevices/SpeechRecognition
 *   - acumulador + interim do STT
 *   - drops total/consecutivos/último reason
 *   - quiet window pós-TTS
 *
 * Princípios:
 *   - Observador puro: NÃO chama nada que altere o pipeline.
 *   - Try/catch em cada leitura (permissions API não existe em todos
 *     browsers; chamadas a debugSnapshot podem retornar null em
 *     race de unmount).
 *   - Painel é collapsável via botão X (estado local, não persistido).
 *
 * Pós-bug fechado: o componente todo vai sob feature flag
 * REACT_APP_VOICE_DEBUG === "true". Por agora roda sempre.
 */
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { isIOS } from "@/utils/platform";

const REFRESH_MS = 150;

// ====================== HEALTH HEURISTICS ======================
// Cor da bolinha por linha. Verde = saudável, amarelo = warning,
// vermelho = crítico. Verdict é baseado APENAS no snapshot mais
// recente — sem histórico, sem inferência cross-line.

const RED = "#ff6b6b";
const YELLOW = "#ffd43b";
const GREEN = "#51cf66";
const GRAY = "#868e96";

function colorForState(state) {
  if (!state) return GRAY;
  // PROCESSING e SPEAKING são estados "ocupados" — esperados, mas se
  // ficarem >35s o watchdog H3 dispara. Aqui mostramos amarelo só pra
  // sinalizar "não está aceitando voz agora".
  if (state === "processing" || state === "speaking" || state === "connecting") return YELLOW;
  if (state === "listening" || state === "idle") return GREEN;
  return GRAY;
}

function colorForPrewarm(prewarm) {
  if (prewarm === "prewarmed") return GREEN;
  if (prewarm === "warming" || prewarm === "cold") return YELLOW;
  if (prewarm === "error" || prewarm === "denied") return RED;
  return GRAY;
}

function colorForPermission(perm) {
  if (perm === "granted") return GREEN;
  if (perm === "prompt") return YELLOW;
  if (perm === "denied") return RED;
  if (perm === "unsupported" || perm === "unknown") return GRAY;
  return GRAY;
}

function colorForBool(value, { trueIsGood = true } = {}) {
  if (value == null) return GRAY;
  if (trueIsGood) return value ? GREEN : RED;
  return value ? RED : GREEN;
}

function colorForDrops(total, consecutive) {
  if (consecutive >= 2) return RED;
  if (total > 0) return YELLOW;
  return GREEN;
}

// VOICE-POLISH-V8 — finish_reason colorido pra detectar regressões.
// "ended" = TTS terminou inteiro (verde).
// "barge_in" = user cortou (amarelo, esperado se intencional).
// "*_timeout" / "*_error" / "*_abort" / "fetch_*" / "speech_synth_*" /
// "pipeline_error" / "safety_*" / "relisten_*" / "bootstrap_*" = ruim.
function colorForFinishReason(reason) {
  if (!reason || reason === "none") return GRAY;
  if (reason === "ended") return GREEN;
  if (reason === "barge_in") return YELLOW;
  if (
    reason.includes("timeout") ||
    reason.includes("error") ||
    reason.includes("abort") ||
    reason.includes("safety") ||
    reason.includes("bootstrap")
  ) return RED;
  return GRAY;
}

// ====================== ATOMS ======================

function Dot({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 6,
        flexShrink: 0,
      }}
      aria-hidden="true"
    />
  );
}

function Line({ color, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        padding: "1px 0",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Dot color={color} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
    </div>
  );
}

// ====================== MAIN ======================

export default function VoiceDebugPanel({
  voiceState,
  prewarmState,
  isOutputSpeaker,
  vadSpeaking,
  transcript,
  debugSnapshot,
}) {
  const [hidden, setHidden] = useState(false);
  const [snap, setSnap] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (hidden) return;
    const tick = () => {
      try {
        const next = typeof debugSnapshot === "function" ? debugSnapshot() : null;
        setSnap(next);
      } catch (_) {
        // não derrubar o painel se snapshot quebrar
        setSnap(null);
      }
    };
    tick();
    tickRef.current = setInterval(tick, REFRESH_MS);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [hidden, debugSnapshot]);

  if (hidden) return null;

  // Prefer dados do snapshot (vivos via setInterval) com fallback nos
  // props reativos do hook (primeira render).
  const stateValue = snap?.state || voiceState || "—";
  const prewarmValue = snap?.prewarm || prewarmState || "—";
  const vadSpeakingValue = snap?.vadSpeaking ?? vadSpeaking ?? false;
  const interimValue = snap?.interim ?? transcript ?? "";
  const accFinal = snap?.accFinal || "";
  const drops = snap?.drops || { total: 0, consecutive: 0, lastReason: null };
  const micEnabled = snap?.micTrackEnabled;
  const micLabel = snap?.micLabel || "—";
  const lastTtsAgo = snap?.lastTtsEndedMsAgo;
  const inQuiet = !!snap?.inQuiet;
  const locked = !!snap?.locked;
  const loading = !!snap?.loading;
  const recRunning = !!snap?.recRunning;
  const permission = snap?.permissionState || "unknown";
  const mediaSupported = !!snap?.mediaDevicesSupported;
  const speechSupported = !!snap?.speechRecognitionSupported;
  const secure = snap?.isSecureContext;
  const lastDropAgo = snap?.lastDropAgoMs;
  const lastDropReason = snap?.lastDropReason;
  // VOICE-POLISH-V8 — TTS finish + stall telemetry.
  const lastTtsFinishReason = snap?.lastTtsFinishReason || "none";
  const lastTtsFinishMsAgo = snap?.lastTtsFinishMsAgo;
  const lastStallText = snap?.lastStallText;
  const lastStallAt = snap?.lastStallEmittedAt;
  const lastStallAgo = lastStallAt ? Date.now() - lastStallAt : null;
  // VOICE-FIX-WARMUP — grace period após audio.play() onde barge-in
  // detection fica suprimida.
  const speakWarmupRemaining = snap?.speakWarmupRemainingMs ?? 0;

  // Trunca interim/accFinal pra caber em ~280px:
  const truncate = (s, n = 32) => (typeof s === "string" && s.length > n ? `${s.slice(0, n)}…` : s || "");

  return (
    <div
      data-testid="voice-debug-panel"
      style={{
        position: "absolute",
        right: 12,
        bottom: 12,
        minWidth: 280,
        maxWidth: 360,
        padding: "10px 28px 10px 12px",
        backgroundColor: "rgba(0, 0, 0, 0.78)",
        color: "#7CFC9F",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 11,
        lineHeight: 1.4,
        borderRadius: 8,
        border: "1px solid rgba(126, 252, 159, 0.18)",
        zIndex: 60,
        pointerEvents: "auto",
        userSelect: "text",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
      }}
    >
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label="Esconder painel de debug de voz"
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 20,
          height: 20,
          padding: 0,
          background: "transparent",
          border: "none",
          color: "#7CFC9F",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0.7,
        }}
        data-testid="voice-debug-hide"
      >
        <X size={14} />
      </button>

      <Line color={colorForState(stateValue)}>
        State: {stateValue}
        {locked ? " ⟁locked" : ""}
        {loading ? " ⟁loading" : ""}
      </Line>
      <Line color={colorForPrewarm(prewarmValue)}>
        Prewarm: {prewarmValue} | speaker:{isOutputSpeaker ? "✓" : "✗"}
      </Line>
      <Line color={colorForBool(micEnabled)}>
        Mic track: {micEnabled === null ? "—" : micEnabled ? "✓" : "✗"} | rec:{recRunning ? "✓" : "✗"}
      </Line>
      <Line color={GRAY}>
        Mic label: {truncate(micLabel, 30)}
      </Line>
      <Line color={vadSpeakingValue ? GREEN : GRAY}>
        VAD speaking: {vadSpeakingValue ? "✓" : "✗"}
      </Line>
      <Line color={lastTtsAgo == null ? GRAY : inQuiet ? YELLOW : GREEN}>
        Last TTS end: {lastTtsAgo == null ? "—" : `${lastTtsAgo}ms ago`}
        {inQuiet ? " (in quiet)" : ""}
      </Line>
      <Line color={accFinal ? GREEN : GRAY}>
        Acc final: "{truncate(accFinal, 28)}"
      </Line>
      <Line color={interimValue ? GREEN : GRAY}>
        Interim: "{truncate(interimValue, 28)}"
      </Line>
      <Line color={colorForDrops(drops.total, drops.consecutive)}>
        Drops: {drops.total} (consec {drops.consecutive}) last:{lastDropReason || "none"}
        {lastDropAgo != null ? ` ${lastDropAgo}ms` : ""}
      </Line>
      <Line color={colorForFinishReason(lastTtsFinishReason)}>
        Last TTS finish: {lastTtsFinishReason}
        {lastTtsFinishMsAgo != null ? ` (${lastTtsFinishMsAgo}ms ago)` : ""}
      </Line>
      <Line color={lastStallText ? GREEN : GRAY}>
        Last stall: {lastStallText ? `"${truncate(lastStallText, 24)}"` : "—"}
        {lastStallAgo != null ? ` ${lastStallAgo}ms` : ""}
      </Line>
      <Line color={speakWarmupRemaining > 0 ? YELLOW : GREEN}>
        TTS warmup: {speakWarmupRemaining > 0 ? `${speakWarmupRemaining}ms left` : "idle"}
      </Line>
      <Line color={colorForPermission(permission)}>
        Permission: {permission}
      </Line>
      <Line color={mediaSupported ? GREEN : RED}>
        MediaDevices: {mediaSupported ? "✓" : "✗"}
      </Line>
      <Line color={speechSupported ? GREEN : RED}>
        SpeechRecognition: {speechSupported ? "✓" : "✗"}
      </Line>
      <Line color={secure === false ? RED : secure ? GREEN : GRAY}>
        SecureContext: {secure === null || secure === undefined ? "—" : secure ? "✓" : "✗"}
      </Line>
      {isIOS() && (
        <>
          <Line color={snap?.ios_vad_starts > 0 ? GREEN : YELLOW}>
            VAD starts/ends: {snap?.ios_vad_starts ?? 0}/{snap?.ios_vad_ends ?? 0}
          </Line>
          <Line color={
            snap?.ios_recorder_init?.startsWith("ok:")
              ? GREEN
              : snap?.ios_recorder_init === "idle"
                ? GRAY
                : RED
          }>
            Recorder init: {snap?.ios_recorder_init || "idle"}
          </Line>
          <Line color={snap?.ios_recorder_state === "recording" ? GREEN : GRAY}>
            Recorder: {snap?.ios_recorder_state || "null"}
          </Line>
          <Line color={
            snap?.ios_last_transcribe?.status === "ok"
              ? GREEN
              : snap?.ios_last_transcribe?.status === "uploading"
                ? YELLOW
                : (snap?.ios_last_transcribe?.status && snap.ios_last_transcribe.status !== "none")
                  ? RED
                  : GRAY
          }>
            Last transcribe: {snap?.ios_last_transcribe?.status || "—"}
            {snap?.ios_last_transcribe?.chars > 0
              ? ` (${snap.ios_last_transcribe.chars} chars)`
              : ""}
          </Line>
          <Line color={
            snap?.last_listen_attempt?.reason === "ok"
              ? GREEN
              : snap?.last_listen_attempt?.reason && snap.last_listen_attempt.reason !== ""
                ? RED
                : GRAY
          }>
            Listen attempt: {snap?.last_listen_attempt?.where || "none"}
            {snap?.last_listen_attempt?.reason
              ? ` (${snap.last_listen_attempt.reason})`
              : ""}
            {snap?.last_listen_attempt?.retries > 0
              ? ` retries=${snap.last_listen_attempt.retries}`
              : ""}
          </Line>
        </>
      )}
    </div>
  );
}
